"use client"

import type React from "react"
import { useState, useEffect } from "react"
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonLoading,
  IonButtons,
  IonBackButton,
  IonSegment,
  IonSegmentButton,
  IonLabel,
  IonToast,
} from "@ionic/react"
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Badge,
  Tooltip,
  TablePagination,
  InputAdornment,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material"
import { styled } from "@mui/material/styles"
import {
  Add,
  Delete,
  Check,
  ArrowBack,
  LocalHospital,
  Search,
  FilterList,
  Visibility,
  Download,
  Refresh,
  CheckCircle,
  Pending,
  Cancel,
  History,
} from "@mui/icons-material"
// import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"

// import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
// import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns"
import { addWarehouseRequest, getMyPurchaseOrderRequests, getWarehouseStocks } from "../../api/accounting/accounting"
import * as VendorService from "../../api/vendor.service"

const StyledCard = styled(Card)(({ theme }) => ({
  margin: theme.spacing(2),
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: theme.shadows[3],
  [theme.breakpoints.down("sm")]: {
    margin: theme.spacing(1),
    padding: theme.spacing(2),
  },
}))

const PageContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1),
  },
}))

// Define types based on your API response
interface RequestItem {
  id: string
  item_id: string
  stock_name: string
  requested_qty: string
  vendor_id: string
  status: string
  audited_qty?: string
  cmd_approved_qty?: string
  supplied_qty?: string
  created_at: string
  updated_at: string
}

interface PurchaseOrder {
  id: number
  po_number: string
  notes: string | null
  status: string
  created_by: string
  requested_by_name: string
  created_at: string
  approved_at: string | null
  approved_by: string | null
  branch_id: string
  business_id: string
  items: RequestItem[]
}

interface StockItem {
  item_id: string
  stock_name: string
  quantity: string
  unit_price?: string
  department_id: string
  last_updated: string
}

interface Vendor {
  vendor_id: string
  vendor_name: string
  phone: string
  email?: string
  status?: number  // 0 = inactive, 1 = active
}

const WarehouseRequisitionSystem: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'create' | 'view'>('create')
  const [stockItems, setStockItems] = useState<StockItem[]>([])
  const [requestItems, setRequestItems] = useState<RequestItem[]>([])
  const [selectedItem, setSelectedItem] = useState<StockItem | null>(null)
  const [selectedVendor, setSelectedVendor] = useState<string>("")
  const [quantity, setQuantity] = useState<string>("")
  const [loading, setLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const [showSubmitDialog, setShowSubmitDialog] = useState(false)
  const [requestNotes, setRequestNotes] = useState("")
  const [vendors, setVendors] = useState<Vendor[]>([])

  // For view page
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([])
  const [filteredOrders, setFilteredOrders] = useState<PurchaseOrder[]>([])
  const [selectedOrder, setSelectedOrder] = useState<PurchaseOrder | null>(null)
  const [showDetailDialog, setShowDetailDialog] = useState(false)
  const [filters, setFilters] = useState({
    status: 'all',
    vendor_id: 'all',
    start_date: null as Date | null,
    end_date: null as Date | null,
    search: '',
  })
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [totalCount, setTotalCount] = useState(0)
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastType, setToastType] = useState<'success' | 'error'>('success')

  const statusOptions = [
    { value: 'all', label: 'All Status', color: 'default' },
    { value: 'draft', label: 'Draft', color: 'default' },
    { value: 'pending', label: 'Pending', color: 'warning' },
    { value: 'approved', label: 'Approved', color: 'success' },
    { value: 'rejected', label: 'Rejected', color: 'error' },
    { value: 'partially_approved', label: 'Partially Approved', color: 'info' },
    { value: 'completed', label: 'Completed', color: 'success' },
  ]

  const workflowSteps = [
    { key: 'po_approval', label: 'PO Approval', field: 'approved_qty' },
    { key: 'cmd_approval', label: 'CMD Approval', field: 'cmd_approved_qty' },
    { key: 'vendor_processing', label: 'Vendor Processing', field: 'supplied_qty' },
    { key: 'audit_review', label: 'Audit Review', field: 'audited_qty' },
  ]

  useEffect(() => {
    fetchVendors()
  }, [])

  useEffect(() => {
    if (activeTab === 'view') {
      fetchPurchaseOrders()
    } else {
      fetchStockItems()
    }
  }, [activeTab, page, rowsPerPage, filters])

  useEffect(() => {
    applyFilters()
  }, [purchaseOrders, filters])

  const fetchVendors = async () => {
    try {
      const response = await VendorService.getAllApprovedVendors()
      console.log('Vendors Responses:', response)

      if (response.data && response.data) {
        // Filter only active vendors (status = 1)
        // const activeVendors = response.data.data
        //   .filter((vendor: any) => vendor.status === 1)
        //   .map((vendor: any) => ({
        //     vendor_id: vendor.vendor_id,
        //     vendor_name: vendor.vendor_name,
        //     phone: vendor.phone,
        //     email: vendor.email,
        //     status: vendor.status
        //   }))
        setVendors(response.data)
      }
    } catch (error) {
      console.error("Failed to fetch vendors", error)
      showErrorToast('Failed to fetch vendors')
    }
  }

  const fetchStockItems = async () => {
    setLoading(true)
    try {
      const response = await getWarehouseStocks()
      console.log('Warehouse Stocks:', response)

      if (response.data && response.data.data) {
        const filtered = searchQuery
          ? response.data.data.filter(
            (item: any) =>
              item.stock_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
              item.item_id?.toLowerCase().includes(searchQuery.toLowerCase()),
          )
          : response.data.data

        setStockItems(filtered)
      }
    } catch (error) {
      console.error("Failed to fetch stock items")
      showErrorToast('Failed to fetch stock items')
    } finally {
      setLoading(false)
    }
  }

  const fetchPurchaseOrders = async () => {
    setLoading(true)
    try {
      const queryParams = {
        page: (page + 1).toString(),
        per_page: rowsPerPage.toString(),
        ...(filters.status !== 'all' && { status: filters.status }),
        ...(filters.search && { search: filters.search }),
      }

      const response = await getMyPurchaseOrderRequests(queryParams)
      console.log('Purchase Order Response:', response)

      if (response.data && response.data.data) {
        const orders = response.data.data.map((order: any) => ({
          id: order.id,
          po_number: order.po_number,
          notes: order.notes,
          status: order.status,
          created_by: order.created_by,
          requested_by_name: order.requested_by_name,
          created_at: order.created_at,
          approved_at: order.approved_at,
          approved_by: order.approved_by,
          branch_id: order.branch_id,
          business_id: order.business_id,
          items: order.items.map((item: any) => ({
            id: item.id,
            item_id: item.item_id,
            stock_name: item.stock_name,
            requested_qty: item.requested_qty,
            vendor_id: item.vendor_id,
            status: item.status,
            audited_qty: item.audited_qty,
            cmd_approved_qty: item.cmd_approved_qty,
            supplied_qty: item.supplied_qty,
            created_at: item.created_at,
            updated_at: item.updated_at,
          }))
        }))

        setPurchaseOrders(orders)
        setTotalCount(orders.length)
      }
    } catch (error) {
      console.error("Failed to fetch purchase orders")
      showErrorToast('Failed to fetch purchase orders')
    } finally {
      setLoading(false)
    }
  }

  const applyFilters = () => {
    let filtered = [...purchaseOrders]

    if (filters.status !== 'all') {
      filtered = filtered.filter(order => order.status === filters.status)
    }

    if (filters.vendor_id !== 'all') {
      filtered = filtered.filter(order => 
        order.items.some(item => item.vendor_id === filters.vendor_id)
      )
    }

    if (filters.search) {
      const query = filters.search.toLowerCase()
      filtered = filtered.filter(order =>
        order.po_number.toLowerCase().includes(query) ||
        order.requested_by_name.toLowerCase().includes(query) ||
        order.items.some(item => item.stock_name.toLowerCase().includes(query))
      )
    }

    setFilteredOrders(filtered)
  }

  const showSuccessToast = (message: string) => {
    setToastMessage(message)
    setToastType('success')
    setShowToast(true)
  }

  const showErrorToast = (message: string) => {
    setToastMessage(message)
    setToastType('error')
    setShowToast(true)
  }

  const addItemToRequest = () => {
    if (!selectedItem || !quantity || parseInt(quantity) <= 0) {
      showErrorToast('Please select an item and enter valid quantity')
      return
    }

    if (!selectedVendor) {
      showErrorToast('Please select a vendor')
      return
    }

    if (parseInt(quantity) > parseInt(selectedItem.quantity || '0')) {
      showErrorToast(`Only ${selectedItem.quantity} items available in stock`)
      return
    }

    const vendor = vendors.find((v) => v.vendor_id === selectedVendor)
    const newItem: RequestItem = {
      id: Date.now().toString(),
      item_id: selectedItem.item_id,
      stock_name: selectedItem.stock_name,
      requested_qty: quantity,
      vendor_id: selectedVendor,
      status: 'pending',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }

    setRequestItems([...requestItems, newItem])
    setSelectedItem(null)
    setSelectedVendor("")
    setQuantity("")
    showSuccessToast('Item added to requisition')
  }

  const removeItemFromRequest = (id: string) => {
    setRequestItems(requestItems.filter((item) => item.id !== id))
    showSuccessToast('Item removed from requisition')
  }

  const calculateTotalValue = () => {
    // Calculate based on your actual pricing data
    return requestItems.reduce((total, item) => {
      // You'll need to get actual prices from your API
      const price = 0 // Replace with actual price from API
      return total + parseInt(item.requested_qty) * price
    }, 0)
  }

  const handleSubmitRequest = async () => {
    if (requestItems.length === 0) {
      showErrorToast('Please add at least one item to request')
      return
    }

    setLoading(true)
    try {
      const requestData = {
        items: requestItems.map(item => ({
          item_id: item.item_id,
          stock_name: item.stock_name,
          quantity: item.requested_qty,
          vendor_id: item.vendor_id,
        })),
        notes: requestNotes,
      }

      console.log('Submitting request:', requestData)
      const response = await addWarehouseRequest(requestData)
      console.log('Response:', response)

      showSuccessToast('Requisition submitted successfully!')
      setRequestItems([])
      setRequestNotes('')
      setShowSubmitDialog(false)
      
      // Refresh purchase orders
      await fetchPurchaseOrders()
      setActiveTab('view')
    } catch (error: any) {
      console.error('Error submitting requisition:', error)
      showErrorToast(error.response?.data?.message || 'Error submitting requisition')
    } finally {
      setLoading(false)
    }
  }

  const handleViewOrder = (order: PurchaseOrder) => {
    setSelectedOrder(order)
    setShowDetailDialog(true)
  }

  const handleFilterChange = (key: string, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }))
    setPage(0)
  }

  const getStatusChip = (status: string) => {
    const statusMap: any = {
      draft: { label: 'Draft', color: 'default', icon: <Pending /> },
      pending: { label: 'Pending', color: 'warning', icon: <Pending /> },
      approved: { label: 'Approved', color: 'success', icon: <CheckCircle /> },
      rejected: { label: 'Rejected', color: 'error', icon: <Cancel /> },
      partially_approved: { label: 'Partially Approved', color: 'info', icon: <CheckCircle /> },
      completed: { label: 'Completed', color: 'success', icon: <CheckCircle /> },
    }

    const statusInfo = statusMap[status] || { label: status, color: 'default', icon: null }

    return (
      <Chip
        label={statusInfo.label}
        color={statusInfo.color as any}
        size="small"
        icon={statusInfo.icon}
        variant="outlined"
      />
    )
  }

  const getItemStatusChip = (item: RequestItem) => {
    const statusMap: any = {
      pending: { label: 'Pending', color: 'warning' },
      approved: { label: 'Approved', color: 'success' },
      rejected: { label: 'Rejected', color: 'error' },
    }

    const statusInfo = statusMap[item.status] || { label: item.status, color: 'default' }

    return (
      <Chip
        label={statusInfo.label}
        color={statusInfo.color as any}
        size="small"
      />
    )
  }

  const getWorkflowProgress = (item: RequestItem) => {
    return workflowSteps.map(step => {
      const qty = item[step.field as keyof RequestItem] as string
      return {
        ...step,
        qty: qty || '0',
        completed: parseFloat(qty || '0') > 0
      }
    })
  }

  const handleUpdateQuantity = (id: string, newQty: string) => {
    setRequestItems(items => items.map(item => {
      if (item.id === id) {
        return { ...item, requested_qty: newQty };
      }
      return item;
    }));
  };

  const renderCreatePage = () => (
    <>
      <Box
        sx={{
          bgcolor: "#0066cc",
          color: "white",
          py: 3,
          px: 4,
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
          <IconButton sx={{ color: "white" }} onClick={() => window.history.back()}>
            <ArrowBack />
          </IconButton>
          <LocalHospital sx={{ fontSize: 32 }} />
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Create New Requisition
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ opacity: 0.9, ml: 7 }}>
          Request medical supplies and equipment from warehouse
        </Typography>
      </Box>

      <Grid container spacing={3} sx={{ mb: 4, mt: 2 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: "#e6f2ff", borderLeft: "4px solid #0066cc" }}>
            <CardContent>
              <Typography variant="h3" sx={{ fontWeight: 700, color: "#0066cc" }}>
                {requestItems.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Items in Cart
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: "#e7f7f3", borderLeft: "4px solid #00a76f" }}>
            <CardContent>
              <Typography variant="h3" sx={{ fontWeight: 700, color: "#00a76f" }}>
                {requestItems.reduce((sum, item) => sum + parseInt(item.requested_qty), 0)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Units
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: "#fff4e5", borderLeft: "4px solid #ff9800" }}>
            <CardContent>
              <Typography variant="h3" sx={{ fontWeight: 700, color: "#ff9800" }}>
                {requestItems.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Vendors
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card sx={{ mb: 4, borderRadius: 2, boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)" }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
            <Search sx={{ color: "#0066cc", fontSize: 28 }} />
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Search & Add Products
            </Typography>
          </Box>

          <Alert severity="info" sx={{ mb: 3, bgcolor: "#e0f7fa" }}>
            Select a product from the catalog, choose your vendor, specify quantity, and add to your requisition list.
          </Alert>

          <Grid container spacing={3}>
            <Grid item xs={12} md={5}>
              <Autocomplete
                options={stockItems}
                getOptionLabel={(option) => `${option.stock_name} (${option.item_id})`}
                value={selectedItem}
                onChange={(_, newValue) => setSelectedItem(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search Product"
                    variant="outlined"
                    fullWidth
                    onChange={(e) => {
                      setSearchQuery(e.target.value)
                      if (!e.target.value) fetchStockItems()
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        fetchStockItems()
                      }
                    }}
                    InputProps={{
                      ...params.InputProps,
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props}>
                    <Box sx={{ width: "100%" }}>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {option.stock_name}
                      </Typography>
                      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          ID: {option.item_id}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Stock: {option.quantity}
                        </Typography>
                      </Box>
                    </Box>
                  </li>
                )}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Select Vendor</InputLabel>
                <Select
                  value={selectedVendor}
                  onChange={(e) => setSelectedVendor(e.target.value)}
                  label="Select Vendor"
                  disabled={!selectedItem}
                >
                  {vendors.map((vendor) => (
                    <MenuItem key={vendor.vendor_id} value={vendor.vendor_id}>
                      <Box>
                        <Typography variant="body2">{vendor.vendor_name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {vendor.phone}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Quantity"
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                inputProps={{
                  min: 1,
                  max: selectedItem ? parseInt(selectedItem.quantity || '0') : undefined,
                }}
                disabled={!selectedItem}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<Add />}
                onClick={addItemToRequest}
                disabled={!selectedItem || !quantity || !selectedVendor}
                sx={{
                  height: "56px",
                  bgcolor: "#0066cc",
                  "&:hover": { bgcolor: "#0052a3" },
                }}
              >
                Add
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {requestItems.length > 0 && (
        <Card sx={{ borderRadius: 2, boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)" }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
              Requisition Items ({requestItems.length})
            </Typography>

            <TableContainer component={Paper} sx={{ boxShadow: "none", border: "1px solid #e0e0e0" }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: "#f5f6fa" }}>
                    <TableCell sx={{ fontWeight: 600 }}>Product</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Vendor</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      Available
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      Quantity
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {requestItems.map((item) => {
                    const stockItem = stockItems.find(s => s.item_id === item.item_id)
                    const vendor = vendors.find(v => v.vendor_id === item.vendor_id)
                    return (
                      <TableRow key={item.id} hover>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {item.stock_name}
                            </Typography>
                            <Chip
                              label={item.item_id}
                              size="small"
                              variant="outlined"
                              sx={{ mt: 0.5, height: 20, fontSize: "0.7rem" }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{vendor?.vendor_name}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            label={stockItem?.quantity || '0'}
                            size="small"
                            sx={{ bgcolor: "#e7f7f3", color: "#00a76f" }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <TextField
                            type="number"
                            size="small"
                            value={item.requested_qty}
                            onChange={(e) => handleUpdateQuantity(item.id, e.target.value)}
                            inputProps={{ min: 1 }}
                            sx={{ width: 100 }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={() => removeItemFromRequest(item.id)}
                            sx={{ color: "#d32f2f" }}
                          >
                            <Delete />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ mt: 3 }}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Additional Notes (Optional)"
                value={requestNotes}
                onChange={(e) => setRequestNotes(e.target.value)}
                placeholder="Add any special instructions or urgency notes..."
              />
            </Box>

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3 }}>
              <Button
                variant="outlined"
                onClick={() => {
                  setRequestItems([])
                  setRequestNotes("")
                }}
                disabled={loading}
                sx={{ px: 4 }}
              >
                Clear All
              </Button>
              <Button
                variant="contained"
                size="large"
                endIcon={<Check />}
                onClick={() => setShowSubmitDialog(true)}
                disabled={requestItems.length === 0 || loading}
                sx={{
                  px: 4,
                  bgcolor: "#00a76f",
                  "&:hover": { bgcolor: "#008c5e" },
                }}
              >
                Submit Requisition
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}
    </>
  )

  const renderViewPage = () => (
    // <LocalizationProvider dateAdapter={AdapterDateFns}>
    <>
      <Box
        sx={{
          bgcolor: "#0066cc",
          color: "white",
          py: 3,
          px: 4,
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
          <IconButton sx={{ color: "white" }} onClick={() => window.history.back()}>
            <ArrowBack />
          </IconButton>
          <LocalHospital sx={{ fontSize: 32 }} />
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Purchase Order Management
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ opacity: 0.9, ml: 7 }}>
          View and manage all purchase orders with approval workflow tracking
        </Typography>
      </Box>

      {/* Filters Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterList /> Filters
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Search PO Number"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  label="Status"
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  {statusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={option.label}
                          size="small"
                          color={option.color as any}
                          variant="outlined"
                        />
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Vendor</InputLabel>
                <Select
                  value={filters.vendor_id}
                  label="Vendor"
                  onChange={(e) => handleFilterChange('vendor_id', e.target.value)}
                >
                  <MenuItem value="all">All Vendors</MenuItem>
                  {vendors.map((vendor) => (
                    <MenuItem key={vendor.user} value={vendor.id}>
                      {vendor.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <DatePicker
                label="Start Date"
                value={filters.start_date}
                onChange={(newValue) => handleFilterChange('start_date', newValue)}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <DatePicker
                label="End Date"
                value={filters.end_date}
                onChange={(newValue) => handleFilterChange('end_date', newValue)}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12} md={1}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => setFilters({
                  status: 'all',
                  vendor_id: 'all',
                  start_date: null,
                  end_date: null,
                  search: '',
                })}
                size="small"
              >
                Clear
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Purchase Orders Table */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5">
              Purchase Orders ({totalCount})
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={fetchPurchaseOrders}
                disabled={loading}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                startIcon={<Download />}
                onClick={() => alert('Export functionality to be implemented')}
              >
                Export
              </Button>
            </Box>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>PO Number</strong></TableCell>
                  <TableCell><strong>Created By</strong></TableCell>
                  <TableCell><strong>Date</strong></TableCell>
                  <TableCell><strong>Items</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Approved By</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : filteredOrders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Alert severity="info">No purchase orders found</Alert>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredOrders.map((order) => (
                    <TableRow key={order.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {order.po_number}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography>{order.requested_by_name}</Typography>
                      </TableCell>
                      <TableCell>
                        {new Date(order.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Typography>{order.items.length} items</Typography>
                      </TableCell>
                      <TableCell>
                        {getStatusChip(order.status)}
                      </TableCell>
                      <TableCell>
                        {order.approved_by ? (
                          <Typography variant="body2">
                            {order.approved_by}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            Not approved yet
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          color="info"
                          onClick={() => handleViewOrder(order)}
                        >
                          <Visibility />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10))
              setPage(0)
            }}
          />
        </CardContent>
      </Card>
    {/* </LocalizationProvider> */}
        </>

  )

  return (
    <IonPage className="pageDown">
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/dashboard" />
          </IonButtons>
          <IonTitle>Requisition</IonTitle>
        </IonToolbar>
        <IonToolbar>
          <IonSegment
            value={activeTab}
            onIonChange={(e) => setActiveTab(e.detail.value as any)}
            style={{ width: '100%' }}
          >
            <IonSegmentButton value="create">
              <IonLabel>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Add fontSize="small" />
                  Create Requisition
                  {requestItems.length > 0 && (
                    <Badge badgeContent={requestItems.length} color="primary" sx={{ ml: 1 }} />
                  )}
                </Box>
              </IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="view">
              <IonLabel>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <History fontSize="small" />
                  View Purchase Orders
                  {purchaseOrders.filter(r => r.status === 'pending').length > 0 && (
                    <Badge
                      badgeContent={purchaseOrders.filter(r => r.status === 'pending').length}
                      color="error"
                      sx={{ ml: 1 }}
                    />
                  )}
                </Box>
              </IonLabel>
            </IonSegmentButton>
          </IonSegment>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <PageContainer>
          {activeTab === 'create' ? renderCreatePage() : renderViewPage()}
        </PageContainer>

        {/* Submit Dialog */}
        <Dialog open={showSubmitDialog} onClose={() => setShowSubmitDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ bgcolor: "#0066cc", color: "white" }}>Confirm Requisition Submission</DialogTitle>
          <DialogContent sx={{ mt: 3 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              You are about to submit a requisition for {requestItems.length} item(s).
            </Alert>

            <Box sx={{ my: 3 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                Items Summary:
              </Typography>
              {requestItems.slice(0, 3).map((item, index) => {
                const vendor = vendors.find(v => v.vendor_id === item.vendor_id)
                return (
                  <Typography key={index} variant="body2" sx={{ ml: 2, my: 0.5 }}>
                    • {item.stock_name} - {item.requested_qty} units from {vendor?.vendor_name}
                  </Typography>
                )
              })}
              {requestItems.length > 3 && (
                <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                  ...and {requestItems.length - 3} more items
                </Typography>
              )}
            </Box>

            {requestNotes && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="subtitle2">Notes:</Typography>
                {requestNotes}
              </Alert>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={() => setShowSubmitDialog(false)} disabled={loading}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmitRequest}
              variant="contained"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <Check />}
              sx={{
                bgcolor: "#00a76f",
                "&:hover": { bgcolor: "#008c5e" },
              }}
            >
              {loading ? "Submitting..." : "Confirm & Submit"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Purchase Order Detail Dialog */}
        <Dialog
          open={showDetailDialog}
          onClose={() => setShowDetailDialog(false)}
          maxWidth="lg"
          fullWidth
          scroll="paper"
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">
                Purchase Order: {selectedOrder?.po_number}
              </Typography>
              {selectedOrder && getStatusChip(selectedOrder.status)}
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            {selectedOrder && (
              <>
                <Grid container spacing={3} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={4}>
                    <Card sx={{ p: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Created By
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {selectedOrder.requested_by_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(selectedOrder.created_at).toLocaleString()}
                      </Typography>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Card sx={{ p: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Status
                      </Typography>
                      <Box sx={{ mt: 1 }}>
                        {getStatusChip(selectedOrder.status)}
                      </Box>
                      {selectedOrder.approved_by && (
                        <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                          Approved by: {selectedOrder.approved_by}
                        </Typography>
                      )}
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Card sx={{ p: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Total Items
                      </Typography>
                      <Typography variant="h5" color="primary" fontWeight="bold">
                        {selectedOrder.items.length}
                      </Typography>
                    </Card>
                  </Grid>
                </Grid>

                {/* Workflow Status Stepper */}
                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                  Approval Workflow
                </Typography>
                <Alert severity="info" sx={{ mb: 2 }}>
                  This purchase order will go through multiple approval stages: PO Approval → CMD Approval → Vendor Processing → Audit Review
                </Alert>

                {/* Items Table */}
                <Typography variant="h6" gutterBottom>
                  Items ({selectedOrder.items.length})
                </Typography>
                <TableContainer component={Paper} sx={{ mb: 3 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Item</strong></TableCell>
                        <TableCell><strong>Vendor</strong></TableCell>
                        <TableCell align="right"><strong>Requested Qty</strong></TableCell>
                        <TableCell align="right"><strong>PO Approved</strong></TableCell>
                        <TableCell align="right"><strong>CMD Approved</strong></TableCell>
                        <TableCell align="right"><strong>Supplied Qty</strong></TableCell>
                        <TableCell align="right"><strong>Audited Qty</strong></TableCell>
                        <TableCell><strong>Status</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedOrder.items.map((item) => {
                        const vendor = vendors.find(v => v.vendor_id === item.vendor_id)
                        return (
                          <TableRow key={item.id}>
                            <TableCell>
                              <Typography>{item.stock_name}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                ID: {item.item_id}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {vendor?.vendor_name || item.vendor_id}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">{item.requested_qty}</TableCell>
                            <TableCell align="right">{item.audited_qty || '-'}</TableCell>
                            <TableCell align="right">{item.cmd_approved_qty || '-'}</TableCell>
                            <TableCell align="right">{item.supplied_qty || '-'}</TableCell>
                            <TableCell align="right">{item.audited_qty || '-'}</TableCell>
                            <TableCell>
                              {getItemStatusChip(item)}
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                {selectedOrder.notes && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Notes:
                    </Typography>
                    <Alert severity="info">
                      {selectedOrder.notes}
                    </Alert>
                  </Box>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowDetailDialog(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        <IonLoading isOpen={loading} message={"Processing..."} />
        <IonToast
          isOpen={showToast}
          onDidDismiss={() => setShowToast(false)}
          message={toastMessage}
          duration={3000}
          color={toastType}
        />
      </IonContent>
    </IonPage>
  )
}

export default WarehouseRequisitionSystem